import { useEffect } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { Box, Flex, Text } from "rebass/styled-components";
import { FiX, FiInfo } from "react-icons/fi";

export interface IClipScoreBreakdownEntry {
  key: string;
  value: string | null;
  score: number | null;
  percentTotal: number | null;
}

interface ScoreBreakdownModalProps {
  open: boolean;
  onClose: () => void;
  clipTitle: string | null;
  totalScore: number | null;
  breakdown: IClipScoreBreakdownEntry[] | null | undefined;
}

const NUMERIC_KEYS = new Set(["score", "totalScore", "total"]);

const isContribution = (e: IClipScoreBreakdownEntry) =>
  typeof e.score === "number" && e.score !== 0 && !NUMERIC_KEYS.has(e.key);

const formatScore = (n: number) =>
  Number.isInteger(n) ? n.toLocaleString() : n.toFixed(2);

const labelFor = (key: string) =>
  key.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const ScoreBreakdownModal = ({
  open,
  onClose,
  clipTitle,
  totalScore,
  breakdown,
}: ScoreBreakdownModalProps) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const contributions = (breakdown ?? []).filter(isContribution);
  const informational = (breakdown ?? []).filter((e) => !isContribution(e));

  return createPortal(
    <Backdrop onClick={onClose} role="dialog" aria-modal="true">
      <Panel onClick={(e) => e.stopPropagation()}>
        <Head>
          <Heading>
            <Eyebrow>Internal · Score Breakdown</Eyebrow>
            <Title>{clipTitle || "Untitled clip"}</Title>
          </Heading>
          <CloseBtn onClick={onClose} aria-label="Close">
            <FiX size={20} />
          </CloseBtn>
        </Head>

        <TotalRow>
          <TotalLabel>Total score</TotalLabel>
          <TotalValue>
            {totalScore != null ? totalScore.toLocaleString() : "—"}
          </TotalValue>
        </TotalRow>

        {contributions.length === 0 && informational.length === 0 ? (
          <Empty>
            <FiInfo size={16} />
            No breakdown available for this clip yet — the scoring engine may
            not have processed it.
          </Empty>
        ) : (
          <>
            {contributions.length > 0 && (
              <Section>
                <SectionLabel>Contributions</SectionLabel>
                <List>
                  {contributions.map((entry, i) => (
                    <Row key={`${entry.key}-${i}`}>
                      <RowKey>{labelFor(entry.key)}</RowKey>
                      {entry.value && entry.value !== entry.key ? (
                        <RowValue>{entry.value}</RowValue>
                      ) : (
                        <span />
                      )}
                      <RowPct>
                        {entry.percentTotal != null
                          ? `${entry.percentTotal}%`
                          : ""}
                      </RowPct>
                      <RowScore>+{formatScore(entry.score ?? 0)}</RowScore>
                    </Row>
                  ))}
                </List>
              </Section>
            )}
            {informational.length > 0 && (
              <Section>
                <SectionLabel>Other signals</SectionLabel>
                <MetaList>
                  {informational.map((entry, i) => (
                    <MetaRow key={`${entry.key}-${i}`}>
                      <MetaKey>{labelFor(entry.key)}</MetaKey>
                      <MetaValue>{entry.value ?? "—"}</MetaValue>
                    </MetaRow>
                  ))}
                </MetaList>
              </Section>
            )}
          </>
        )}
      </Panel>
    </Backdrop>,
    document.body,
  );
};

const Backdrop = styled(Flex)`
  position: fixed;
  inset: 0;
  background: rgba(8, 12, 16, 0.7);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483000;
  padding: 24px;
`;

const Panel = styled(Box)`
  width: 100%;
  max-width: 400px;
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.darkerNavy};
  border: 1px solid rgba(143, 169, 175, 0.16);
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.6);
  padding: 18px 18px 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Head = styled(Flex)`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
`;

const Heading = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const Eyebrow = styled(Text)`
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.envy};
`;

const Title = styled(Text)`
  font-size: 15.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ice};
  line-height: 1.25;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: 1px solid rgba(143, 169, 175, 0.18);
  color: ${({ theme }) => theme.colors.chalk};
  border-radius: 8px;
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: rgba(143, 169, 175, 0.1);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const TotalRow = styled(Flex)`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 10px 12px;
  background: ${({ theme }) => theme.colors.envyAlpha10};
  border: 1px solid ${({ theme }) => theme.colors.envyAlpha40};
  border-radius: 10px;
`;

const TotalLabel = styled(Text)`
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.chalk};
`;

const TotalValue = styled(Text)`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.envy};
  font-variant-numeric: tabular-nums;
`;

const Section = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionLabel = styled(Text)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.chalk};
`;

const List = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled(Box)`
  display: grid;
  grid-template-columns: 1fr auto auto auto;
  gap: 10px;
  align-items: baseline;
  padding: 7px 10px;
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid rgba(143, 169, 175, 0.06);
  border-radius: 8px;
  font-variant-numeric: tabular-nums;
`;

const RowKey = styled(Text)`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.ice};
`;

const RowValue = styled(Text)`
  font-size: 11.5px;
  color: ${({ theme }) => theme.colors.chalk};
`;

const RowScore = styled(Text)`
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.envy};
`;

const RowPct = styled(Text)`
  font-size: 11.5px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.chalk};
  min-width: 32px;
  text-align: right;
`;

const MetaList = styled(Box)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
`;

const MetaRow = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px 10px;
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid rgba(143, 169, 175, 0.06);
  border-radius: 8px;
`;

const MetaKey = styled(Text)`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.chalk};
`;

const MetaValue = styled(Text)`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.ice};
  word-break: break-word;
`;

const Empty = styled(Flex)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 16px;
  background: ${({ theme }) => theme.colors.ned};
  border-radius: 10px;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;

  svg {
    flex-shrink: 0;
  }
`;
